import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext
} from 'react';
import { useParams } from 'react-router-dom';
import { useEvent } from 'features/events/eventHooks';
import styles from './styles.module.scss';
import { Share } from 'components/Share';
import { EventDate } from 'features/events/EventDate';
import { useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { ReactComponent as AudioLoading } from 'images/audio-loading.svg';
import { EventInfo } from 'features/events/EventInfo';
import { ButtonLink } from 'components/Buttons/ButtonLink';
import { setInfoVisible } from 'features/events/eventsSlice';
import { useDispatch } from 'react-redux';

export const EventCountdown: FunctionComponent = () => {
  const { id } = useParams();
  const event = useEvent(id);
  const nativeEventStarted = useSelector(
    (state: RootState) => state.events.nativeEventStarted
  );
  const channel = useSelector(
    (state: RootState) => state.channel.channelDetails
  );
  const nativeEventStopped = useSelector(
    (state: RootState) => state.events.nativeEventStopped
  );
  const { isCreatorAppWebview, isEmbed } = useContext(UserAgentContext);

  const calculateTimeLeft = (): string | undefined => {
    return event?.durationToEvent()?.toFormat('hh:mm:ss');
  };
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [isOverdue, setIsOverdue] = useState(event?.isOverdue());
  const dispatch = useDispatch();

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
      setIsOverdue(event?.isOverdue());
    }, 1000);
    // Clear timeout if the component is unmounted
    return () => clearTimeout(timer);
  });

  const Overdue = () => {
    if (event?.endedAt) {
      return (
        <>
          {isCreatorAppWebview ? (
            <p>Restart your live event</p>
          ) : (
            <p>This live event has ended, stay tuned for the next!</p>
          )}
        </>
      );
    }
    return null;
  };

  if (!event) {
    return null;
  }

  if (isCreatorAppWebview && nativeEventStarted && !nativeEventStopped) {
    return (
      <div className={styles['countdown']}>
        <AudioLoading />
        <h3>Connecting audio</h3>
      </div>
    );
  }

  if (isCreatorAppWebview && nativeEventStopped) {
    return null;
  }

  if (event.isAdhoc()) {
    return (
      <div className={styles['countdown']}>
        {isCreatorAppWebview && !event?.endedAt ? (
          <>
            <p>
              Click <strong>Start</strong> to go live with...
            </p>
            <h2>{event.title}</h2>
            <Share
              title={event.title}
              label={'event'}
              showLabel={true}
              fallbackUrl={channel?.legacyLivepageUrl}
            />
          </>
        ) : (
          <Overdue />
        )}
      </div>
    );
  }

  return (
    <div className={styles['countdown']}>
      {!event?.endedAt && !isCreatorAppWebview && <span>Next event:</span>}
      <h2 className="mar-n truncate-multiline">{event.title}</h2>
      <div className={styles['countdown__date']}>
        <EventDate eventId={event.id} hideTime={event.isIn24hrs()} />
      </div>
      {!isEmbed && (
        <ButtonLink
          label={'View event details'}
          icon={'info'}
          iconSize={16}
          onClick={() => dispatch(setInfoVisible(true))}
        />
      )}
      {isOverdue ? (
        <Overdue />
      ) : (
        <div>
          {event.isIn24hrs() && (
            <div className={styles['countdown__time']}>{timeLeft}</div>
          )}
          <Share
            title={event.title}
            label={'event'}
            showLabel={true}
            fallbackUrl={channel?.legacyLivepageUrl}
          />
        </div>
      )}
      <EventInfo event={event} />
    </div>
  );
};
